import React, { useState, useEffect, useContext } from "react";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { getNameInitials } from "../constants/globalFunctions";
import IconComponent from "../icons";
import { ThemeContext } from "../../ProviderApp";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import UserProfileModal from "../../components/UserProfileModalComponent";
import { fetchUserTagData } from "../../actions/setUserTagData";
import { openExternalUser } from "../../reducers/userProfileModal";
import AvatarComponent, { AvatarSize } from "./AvatarComponent";
import _ from "lodash";
import { RootState } from "@Reducers/index";
import { Box } from "@mui/material";
import { TranslatedString } from "@i18n";
import GroupedUsersDetailsModal from "@Components/UserProfileModalComponent/GroupedUsersDetailsModal";
import { objectIdString } from "@Helpers/schemas/common.schema";

/**
 * `size` is a loose `string` on this shared tag's props (~80 call sites, many of them untyped grid
 * rows), so it is checked against the closed `AvatarSize` union rather than asserted into it. An
 * unrecognised size becomes `undefined`, which is what `AvatarComponent` already falls back on.
 */
const AVATAR_SIZES = ["XXS", "XS", "LSM", "SM", "LG", "LLG", "XL", "GR"] as const satisfies readonly AvatarSize[];

const toAvatarSize = (value: string | undefined): AvatarSize | undefined =>
  AVATAR_SIZES.find((avatarSize) => avatarSize === value);

// Type definitions for the props with `readonly` modifier
export interface UserNameTagComponentWithProviderProps {
  readonly id?: string;
  readonly handleDeleteIconClicked?: () => void;
  readonly showAvatarOnly?: boolean;
  readonly size?: string;
  readonly isCircle?: boolean;
  readonly isSquare?: boolean;
  readonly bgColor?: string;
  readonly isExternal?: boolean;
  readonly style?: React.CSSProperties;
  readonly maxAvatars?: number;
  readonly userIdsForGroup?: string[];
  readonly fullName?: string;
  /** Email of an external (no-`id`) user — e.g. an email-sourced support ticket
   *  reporter. When set and there is no `id`, clicking the chip opens a profile
   *  modal showing the name + email. */
  readonly email?: string;
  readonly chipBackground?: string;
  readonly chipBorder?: string;
  readonly enableDetailsModalForGroupedUsers?: boolean;
  readonly detailsModalHeader?: TranslatedString;
}

function UserNameTagComponentWithProvider({
  id,
  handleDeleteIconClicked,
  showAvatarOnly = false,
  size,
  isCircle = true,
  isSquare = false,
  bgColor = "var(--system-colour-2)",
  isExternal = false,
  style,
  maxAvatars,
  userIdsForGroup = [],
  fullName,
  email,
  chipBackground,
  chipBorder,
  enableDetailsModalForGroupedUsers,
  detailsModalHeader,
}: UserNameTagComponentWithProviderProps) {
  const { theme } = useContext(ThemeContext);
  const dispatch = useDispatch();
  const userTagData = useSelector((state: RootState) => state.userTagData); // Adjusted for state type

  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [showGroupedUserDetailsModal, setShowGroupedUserDetailsModal] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (id && userFullName) {
      setShowUserProfileModal(true);
    } else if (!id && email) {
      // External (email-sourced) user with no account: show name + email in the modal.
      dispatch(openExternalUser({ fullName, email }));
    }
  };

  const handleGroupedAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowGroupedUserDetailsModal(true);
  };

  const onClose = () => {
    setShowUserProfileModal(false);
  };

  useEffect(() => {
    if (id) {
      // `id` is this shared user-tag's loose `string` prop, passed from ~80 call sites (many of them
      // untyped grid rows), so this is the boundary where the account id enters the type system:
      // validate it instead of branding it, otherwise a grid row id would be fetched as an account.
      // Dropping the request for a non-ObjectId `id` costs nothing a user could see: the route
      // (`getUserDetailsByIdRequest`, packages/server/accounts/accounts.schema.ts) validates
      // `userId` with this same `objectIdString`, so such a call was already rejected server-side,
      // `isLoaded` never flipped, and the only trace left was a stuck `{ isLoading: true }` entry —
      // exactly what the "0" sentinel note in ListSpaces/listSpaces.tsx describes.
      const accountId = objectIdString.safeParse(id);
      if (accountId.success) {
        dispatch(fetchUserTagData(accountId.data));
      }
    }
  }, [id, dispatch]);

  const getGroupedAvatars = (ids: string[]) => {
    return ids.map((userId: string) => (
      <AvatarComponent
        key={userId}
        fullName={fullName}
        id={userId}
        size={toAvatarSize(size)}
        isCircle={isCircle}
        bgColor={bgColor}
      />
    ));
  };

  let groupedAvatars;

  if (maxAvatars) {
    groupedAvatars = getGroupedAvatars(userIdsForGroup);
  }

  const isLoaded = id ? _.get(userTagData, [id, "isLoaded"]) : undefined;
  const userData = id ? userTagData[id]?.data : undefined;
  const profilePic = userData?.profilePic;
  const userFullName = userData?.fullName;
  const firstName = userData?.firstName;
  const lastName = userData?.lastName;

  function getInitials() {
    if (id && (firstName || lastName)) {
      return getNameInitials({ firstName, lastName });
    }
    return getNameInitials({
      firstName: fullName?.split(" ")[0] || "",
      lastName: fullName?.split(" ")[1] || "",
    });
  }

  return (
    <>
      {(isLoaded || fullName) &&
        (showAvatarOnly ? (
          <AvatarComponent
            bgColor={bgColor}
            size={toAvatarSize(size)}
            isCircle={isCircle}
            isSquare={isSquare}
            id={id}
            fullName={fullName}
            style={style}
            maxAvatars={undefined}
            groupedAvatars={undefined}
          />
        ) : (
          <Chip
            label={id && userFullName ? userFullName : (fullName ?? "")}
            deleteIcon={<IconComponent name="cross" width="1rem" height="1rem" />}
            size="medium"
            avatar={
              id && profilePic ? (
                <Avatar src={profilePic} />
              ) : (
                <Avatar>
                  <div className="text-white">
                    {isExternal ? (
                      <div className="bg-orange-400 rounded-full w-8 h-8 flex items-center justify-center">
                        <IconComponent name="userProfileWhite" width="1.25rem" height="1.25rem" />
                      </div>
                    ) : (
                      getInitials()
                    )}
                  </div>
                </Avatar>
              )
            }
            onDelete={handleDeleteIconClicked}
            onClick={handleClick}
            sx={{
              background: chipBackground,
              border: chipBorder,
              color: `var(${theme.rootTextColor})`,
              lineHeight: "normal",
              ...(isExternal && {
                "&:hover": {
                  backgroundColor: chipBackground,
                },
              }),
            }}
          />
        ))}
      {showUserProfileModal && id && <UserProfileModal userId={id} onClose={onClose} />}

      {maxAvatars && (
        <Box onClick={enableDetailsModalForGroupedUsers ? handleGroupedAvatarClick : undefined}>
          <AvatarComponent
            style={style}
            bgColor={bgColor}
            size={toAvatarSize(size)}
            isCircle={isCircle}
            isSquare={isSquare}
            maxAvatars={maxAvatars}
            groupedAvatars={groupedAvatars}
            userIdsForGroup={userIdsForGroup}
          />
        </Box>
      )}
      {showGroupedUserDetailsModal && userIdsForGroup.length && (
        <GroupedUsersDetailsModal
          detailsModalHeader={detailsModalHeader}
          ids={userIdsForGroup}
          closeModal={() => setShowGroupedUserDetailsModal(false)}
        />
      )}
    </>
  );
}

export default UserNameTagComponentWithProvider;
